/**
 * Tous les textes visibles du back-office.
 *
 * Aucun texte visible ne doit être écrit en dur dans un composant.
 * Libellés de statut et d'action : docs/13-glossaire.md, sections 4 et 5.
 * Messages types : docs/15-ui-ux.md, section 7.
 */

import type { BoardColumn, OrderAction, OrderStatus, ReportPeriod } from '@scan2eat/shared';
import type { PaymentMethod } from '@scan2eat/shared';

/**
 * Duree en toutes lettres, pour une alerte.
 *
 * « 95 minutes » se lit mal quand on cherche a juger vite ; « 1 h 35 » se lit
 * d'un coup d'oeil.
 */
function formatDelay(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) return `${hours} h`;
  return `${hours} h ${String(rest).padStart(2, '0')}`;
}

export const labels = {
  app: {
    name: 'Scan2Eat',
    tableLabel: (label: string) => `Table ${label}`,
  },

  common: {
    close: 'Fermer',
    more: "Plus d'actions",
    seeAll: 'Voir tout',
    seeLess: 'Réduire',
  },

  nav: {
    label: 'Navigation du back-office',
    heading: 'Service',
    skipToContent: 'Aller au contenu',
    openMenu: 'Ouvrir la navigation',
    closeMenu: 'Fermer la navigation',
    pendingCount: (n: number) => `${n} nouvelle${n > 1 ? 's' : ''} commande${n > 1 ? 's' : ''}`,
    board: 'Commandes',
    history: 'Historique',
    menu: 'Menu',
    tables: 'Tables',
    report: 'Bilan',
    team: 'Équipe',
    balance: 'Solde',
    logout: 'Se déconnecter',
    profile: 'Mon profil',
  },

  /** Solde du compte marchand, en lecture seule. Voir docs/adr/0019. */
  balance: {
    title: 'Solde de mon compte',
    intro:
      "Le solde de votre compte PayDunya, alimenté par les paiements mobile money de vos clients.",
    available: 'Solde disponible',
    refresh: 'Actualiser',
    readAt: 'Lu à',
    approximate:
      "Montant approximatif, communiqué par PayDunya. Le solde exact se lit dans votre tableau de bord PayDunya.",
    testMode:
      "Compte en mode test : les paiements sont simulés, ce solde ne correspond à aucun argent réel.",
    unavailable:
      "PayDunya ne répond pas pour le moment. Votre solde n'est pas affiché, mais vos paiements ne sont pas affectés.",
    unknown: 'Solde indisponible',
    unknownDetail:
      "Vos paiements fonctionnent, mais PayDunya ne nous communique pas votre solde : la consultation dépend de l'API de déboursement, à activer depuis votre tableau de bord PayDunya. Votre solde exact s'y lit de toute façon.",
    unknownTest:
      "En mode test, PayDunya ne communique aucun solde : son bac à sable ne couvre pas la consultation de compte. C'est normal, et sans effet sur les paiements simulés. Le solde apparaîtra au passage en production.",
    notConfigured: "Votre compte d'encaissement n'est pas encore configuré.",
    notConfiguredDetail:
      "Vos clés PayDunya nous sont transmises à l'inscription, et nous les enregistrons pour vous. En attendant, vos clients peuvent payer au comptoir, et le mobile money reste annoncé à votre personnel pour vérification.",
  },

  /** Ecran d'invitation, ouvert depuis le lien recu par courriel. Voir docs/adr/0024. */
  invitation: {
    title: 'Bienvenue sur Scan2Eat',
    intro: (restaurant: string) => `Choisissez le mot de passe de votre espace ${restaurant}.`,
    greeting: (firstName: string) => `Bonjour ${firstName},`,
    emailLabel: 'Votre identifiant',
    password: 'Mot de passe',
    passwordRule: 'Dix caractères au minimum, dont une minuscule, une majuscule et un chiffre.',
    confirm: 'Confirmez le mot de passe',
    mismatch: 'Les deux mots de passe ne sont pas identiques.',
    tooWeak: 'Ce mot de passe ne respecte pas les règles ci-dessus.',
    submit: 'Choisir ce mot de passe',
    submitting: 'Enregistrement',
    checking: 'Vérification du lien',
    doneTitle: 'Votre mot de passe est enregistré',
    doneText: 'Connectez-vous avec votre adresse et le mot de passe que vous venez de choisir.',
    goToLogin: 'Aller à la connexion',
    invalidTitle: "Ce lien n'est plus valable",
    invalidText:
      'Il a déjà servi, ou il n\'a jamais existé. Si vous avez déjà choisi votre mot de passe, connectez-vous ; sinon, demandez une nouvelle invitation à Scan2Eat.',
    expiredTitle: 'Ce lien a expiré',
    expiredText:
      'Les invitations ne restent valables que quelques jours. Demandez-en une nouvelle à Scan2Eat, elle arrivera sur la même adresse.',
  },

  theme: {
    light: 'Thème clair',
    dark: 'Thème sombre',
    toggle: 'Changer de thème',
  },

  /** Ecran d'accueil d'un administrateur de plateforme sans restaurant courant. */
  chooseRestaurant: {
    switcher: 'Restaurant affiché',
    placeholder: 'Choisir un restaurant',
    title: 'Choisissez un restaurant',
    help: "Votre compte administre la plateforme, pas un restaurant en particulier. Choisissez-en un dans la barre latérale pour accéder à son back-office. L'ouverture des restaurants se fait depuis le back-office de plateforme.",
  },

  /** Signalement du mot de passe provisoire. Voir docs/adr/0014. */
  mustChangePassword: {
    banner: 'Votre mot de passe est provisoire. Changez-le depuis votre profil.',
    action: 'Changer maintenant',
  },

  /** Cloche des notifications. Voir docs/adr/0013. */
  notifications: {
    aria: 'Notifications',
    ariaWithCount: (n: number) => `Notifications, ${n} non lue${n > 1 ? 's' : ''}`,
    title: 'Notifications',
    markAllRead: 'Tout marquer comme lu',
    empty: 'Aucune notification.',
    unpaidTitle: (table: string) => `Table ${table} — commandes impayées`,
    unpaidBody: (count: number, total: string) =>
      `${count} commande${count > 1 ? 's' : ''} restée${count > 1 ? 's' : ''} impayée${count > 1 ? 's' : ''}, ${total}. La session a expiré.`,
    /**
     * Ecarter les commandes d'une table abandonnee.
     *
     * L'action annule chaque commande encore en cours par la machine a etats.
     * Elle est definitive, et n'appartient qu'au personnel : le restaurant doit
     * voir la perte et decider lui-meme. Voir docs/06, section 8, et
     * docs/adr/0016-ecarter-les-commandes-d-une-table-abandonnee.md.
     */
    discard: 'Écarter les commandes',
    discardAria: (table: string) => `Écarter les commandes de la table ${table}`,
    discardTitle: (table: string) => `Écarter les commandes de la table ${table}`,
    discardDescription: (count: number) =>
      count > 1
        ? `${count} commandes sont restées en cours sur cette table. Les écarter les annule définitivement : elles quittent le tableau des commandes et n'entrent pas au chiffre d'affaires.`
        : `Une commande est restée en cours sur cette table. L'écarter l'annule définitivement : elle quitte le tableau des commandes et n'entre pas au chiffre d'affaires.`,
    discardReasonLabel: 'Motif (facultatif)',
    discardReasonHint: (fallback: string) => `Sans précision, le motif enregistré sera « ${fallback} ».`,
    discardConfirm: 'Écarter définitivement',
    discardAbort: 'Revenir',
    discardDone: (count: number) =>
      `${count} commande${count > 1 ? 's' : ''} écartée${count > 1 ? 's' : ''}.`,
    discardNone: 'Aucune commande à écarter : elles ont déjà été traitées.',
    discardHandled: 'Commandes écartées.',

    /**
     * Commande immobilisee trop longtemps a une etape.
     *
     * Le personnel choisit : annuler, ou garder. Garder n'est pas ignorer,
     * c'est repondre -- l'alerte s'eteint pour cet episode, la commande ne
     * bouge pas. Voir docs/adr/0017-alerter-sur-une-commande-immobilisee.md.
     */
    stalledTitle: (number: number, table: string) =>
      `Commande n° ${number} — table ${table}`,
    stalledBody: (step: string, minutes: number) =>
      `Bloquée à « ${step} » depuis ${formatDelay(minutes)}.`,
    stalledCancel: 'Annuler',
    stalledCancelAria: (number: number) => `Annuler la commande n° ${number}`,
    stalledKeep: 'Garder',
    stalledKeepAria: (number: number) => `Garder la commande n° ${number}`,
    stalledHandled: 'Alerte traitée.',
    stalledCancelTitle: (number: number) => `Annuler la commande n° ${number}`,
    stalledCancelDescription: (step: string, minutes: number) =>
      `Cette commande est bloquée à « ${step} » depuis ${formatDelay(minutes)}. L'annuler est définitif : elle quitte le tableau des commandes et n'entre pas au chiffre d'affaires.`,
    stalledCancelConfirm: 'Annuler la commande',
    stalledCancelDone: (number: number) => `Commande n° ${number} annulée.`,
    stalledKeptDone: (number: number) => `Commande n° ${number} gardée.`,
    stalledCancelForbidden:
      "Cette commande a dépassé la prise de commande : seul un responsable peut l'annuler. Demandez-lui de le faire depuis son compte.",
    stalledCancelConflict: 'Cette commande a changé entre-temps. Actualisez la liste, puis réessayez.',
    stalledCancelFailed: "La commande n'a pas pu être annulée. Réessayez dans un instant.",
    discardForbidden:
      "Ces commandes ont dépassé la prise de commande : seul un responsable peut les écarter. Demandez-lui de le faire depuis son compte.",
    discardSessionOpen:
      "Cette table est encore attablée : sa session n'est pas close. Ses commandes ne peuvent pas être écartées.",
    discardConflict: 'Ces commandes ont changé entre-temps. Actualisez la liste, puis réessayez.',
    discardFailed: "Les commandes n'ont pas pu être écartées. Réessayez dans un instant.",
  },

  /** Libellés des cinq colonnes du kanban. Voir docs/06, section 4. */
  columns: {
    NEW: 'Nouvelles',
    IN_PROGRESS: 'En cuisine',
    READY: 'Prêtes',
    SERVED: 'À table',
    COMPLETED: 'Terminées',
  } satisfies Record<BoardColumn, string>,

  /** Vérification des paiements déclarés par les convives. Voir docs/06, section 10. */
  payment: {
    onHold: 'En attente',
    awaitingCustomer: 'En attente du choix de paiement du client',
    awaitingNewDeclaration: 'Paiement rejeté, le client doit recommencer',
    counter: 'Au comptoir',
    mobileMoney: 'Mobile Money',
    reference: 'Réf.',
    payerPhone: 'Tél.',
    confirm: 'Confirmer',
    cashIn: 'Encaisser',
    reject: 'Rejeter',
    awaitingProvider: 'Paiement en cours, validation attendue du client',
    rejectPrompt: 'Motif du rejet',
    rejected: 'Paiement rejeté',
    toVerify: 'À vérifier',
    rejectTitle: 'Rejeter le paiement',
    rejectDescription: 'Le client verra ce motif et pourra déclarer son paiement à nouveau.',
    rejectRequired: 'Indiquez le motif du rejet.',
    statusLabels: {
      DECLARED: 'Déclaré par le client, à vérifier',
      CONFIRMED: 'Confirmé',
      REJECTED: 'Rejeté',
    },
    method: 'Moyen de paiement',
    methodLabels: {
      MOBILE_MONEY: 'Mobile Money',
      COUNTER: 'Au comptoir',
      CASH: 'Espèces',
      CARD: 'Carte',
      OTHER: 'Autre',
    },
    providerLabels: {
      MTN: 'MTN MoMo',
      MOOV: 'Moov Money',
      CELTIIS: 'Celtiis Cash',
    },
  },

  /** Libellés admin des sept statuts. Voir docs/13, section 4. */
  status: {
    PENDING: 'Nouvelle',
    IN_PREPARATION: 'En préparation',
    ON_HOLD: 'En attente',
    READY: 'Prête',
    SERVED: 'Servie',
    COMPLETED: 'Terminée',
    CANCELLED: 'Annulée',
  } satisfies Record<OrderStatus, string>,

  /** Libellés des boutons d'action. Voir docs/13, section 5. */
  actions: {
    START: 'Lancer',
    HOLD: 'Mettre en attente',
    RESUME: 'Reprendre',
    READY: 'Prête',
    SERVE: 'Servie',
    COMPLETE: 'Encaisser et terminer',
    CANCEL: 'Annuler',
  } satisfies Record<OrderAction, string>,

  order: {
    number: (n: number) => `Commande n° ${n}`,
  },

  detail: {
    title: 'Détail de la commande',
    close: 'Fermer le détail',
    placedAt: (time: string) => `Passée à ${time}`,
    subtotal: 'Total HT',
    tax: 'TVA',
    total: 'TOTAL TTC',
    customerNote: 'Note du client',
    timeline: 'Historique',
    from: (status: string) => `depuis « ${status} »`,
    actor: (actor: string, userName: string | null) =>
      actor === 'CUSTOMER' ? 'Client' : actor === 'SYSTEM' ? 'Système' : `${userName ?? 'Personnel'}`,
  },

  board: {
    title: 'Commandes',
    greeting: (name: string) => `Bonjour ${name}.`,
    subtitle: (name: string, n: number) =>
      `Bonjour ${name}, ${n} commande${n > 1 ? 's' : ''} en cours.`,
    columnEmpty: {
      NEW: 'Les nouvelles commandes arrivent ici.',
      IN_PROGRESS: 'Rien en cuisine pour le moment.',
      READY: 'Aucun plat en attente de service.',
      SERVED: 'Aucune table à encaisser.',
      COMPLETED: 'Aucune commande terminée.',
    } satisfies Record<BoardColumn, string>,
    moreItems: (n: number) => `+ ${n} autre${n > 1 ? 's' : ''} article${n > 1 ? 's' : ''}`,
    empty: 'Aucune commande en cours. Les nouvelles commandes apparaîtront ici.',
    seeAll: 'Voir tout',
    soundOn: 'Son actif',
    soundOff: 'Son coupé',
    online: 'En ligne',
    reconnecting: 'Reconnexion',
    refresh: 'Actualiser',
    moreActions: 'Plus d\'actions',
    orderNumber: (n: number) => `n° ${n}`,
    newOrderAnnounced: (n: number) => `Nouvelle commande n° ${n}`,
    itemCount: (n: number) => `${n} article${n > 1 ? 's' : ''}`,
    age: 'Depuis',
    editTime: 'Modifier l\'heure de passage',
    completedAt: (time: string) => `Terminée à ${time}`,
  },

  cancel: {
    title: 'Annuler la commande',
    description: "L'annulation est définitive : la commande quitte le tableau et n'entre pas au chiffre d'affaires.",
    reasonLabel: "Motif d'annulation",
    reasonRequired: 'Un motif est obligatoire pour annuler une commande.',
    confirm: "Confirmer l'annulation",
    abort: 'Revenir',
  },

  confirm: {
    title: 'Confirmation',
    confirm: 'Confirmer',
    abort: 'Annuler',
  },

  menu: {
    title: 'Menu',
    subtitle: 'Catégories, plats et disponibilités.',
    summary: (items: number, soldOut: number) =>
      `${items} plat${items > 1 ? 's' : ''}${soldOut > 0 ? ` · ${soldOut} épuisé${soldOut > 1 ? 's' : ''}` : ''}`,
    readOnlyHint: 'Vous pouvez marquer un plat épuisé. La modification du menu est réservée aux responsables.',
    categories: 'Catégories',
    categoryPlaceholder: 'Entrées, Plats, Boissons...',
    categoryActions: (name: string) => `Actions de la catégorie ${name}`,
    itemActions: (name: string) => `Actions du plat ${name}`,
    availabilityFor: (name: string) => `${name} disponible`,
    deleteCategory: 'Supprimer la catégorie',
    deleteCategoryHelp: 'Une catégorie doit être vide pour être supprimée.',
    deleteItemHelp: 'Le plat disparaît du menu. Les commandes déjà passées le conservent.',
    descriptionPlaceholder: 'Ingrédients, accompagnement...',
    noPhoto: 'Aucune photo',
    available: 'Disponible',
    unavailable: 'Épuisé',
    addCategory: 'Ajouter une catégorie',
    categoryName: 'Nom de la catégorie',
    renameCategory: 'Renommer la catégorie',
    moveUp: 'Monter',
    moveDown: 'Descendre',
    deleteCategoryConfirm: (name: string) => `Supprimer la catégorie « ${name} » ?`,
    addItem: 'Ajouter un plat',
    editItem: 'Modifier le plat',
    itemName: 'Nom',
    itemDescription: 'Description',
    itemPrice: 'Prix',
    itemPhoto: 'Photo du plat',
    choosePhoto: 'Choisir une photo',
    replacePhoto: 'Remplacer la photo',
    removePhoto: 'Retirer la photo',
    photoHint: 'JPEG, PNG ou WebP, 5 Mo maximum. La photo est réduite avant envoi.',
    photoPending: 'Sera envoyée à l\'enregistrement',
    photoUploading: 'Envoi de la photo…',
    photoFailed: 'Le plat est enregistré, mais la photo n\'a pas pu être envoyée.',
    itemImage: 'Ou adresse d\'une photo déjà en ligne',
    itemInvalidPrice: 'Le prix accepte au maximum deux décimales.',
    moveItemUp: 'Monter le plat',
    moveItemDown: 'Descendre le plat',
    deleteItem: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    emptyMenu: 'Aucune catégorie. Ajoutez la première pour construire le menu.',
    emptyCategory: 'Aucun plat dans cette catégorie.',
    deleteItemConfirm: (name: string) => `Supprimer définitivement le plat « ${name} » ?`,
    categoryNotEmpty: "Cette catégorie contient encore des plats. Déplacez-les d'abord.",
  },

  tables: {
    title: 'Tables et QR codes',
    subtitle: 'Un QR code par table. Les convives y accèdent au menu sans installation.',
    addHelp: 'Le QR code de la table est généré automatiquement.',
    kpiTables: 'Tables en service',
    kpiOccupied: (n: number) => `${n} occupée${n > 1 ? 's' : ''}`,
    kpiDiners: 'Convives connectés',
    kpiOrders: 'Commandes en cours',
    kpiPending: 'Montant en cours',
    noZone: 'Sans zone',
    zonePlaceholder: 'Salle, Terrasse...',
    capacity: (n: number) => `${n} place${n > 1 ? 's' : ''}`,
    noCapacity: 'Capacité non renseignée',
    pendingOrders: (n: number) => `${n} commande${n > 1 ? 's' : ''} en cours`,
    noActiveOrder: 'Aucune commande en cours',
    moreActionsFor: (label: string) => `Actions de la table ${label}`,
    tentMessage: 'Scannez pour voir notre menu',
    tokenLabel: 'Code de la table',
    copiedShort: 'Copié',
    rotateTitle: (label: string) => `Régénérer le QR code de la table ${label} ?`,
    closeSessionTitle: (label: string) => `Libérer la table ${label} ?`,
    closeSessionHelp: 'Les sessions ouvertes sur cette table sont clôturées. Les convives devront rescanner le QR code pour commander.',
    deleteTableHelp: 'La suppression est refusée si la table porte des commandes.',
    openedAt: (time: string) => `Depuis ${time}`,
    addTable: 'Ajouter une table',
    editTable: 'Modifier la table',
    tableLabelField: 'Libellé',
    tableCapacity: 'Capacité',
    tableZone: 'Zone',
    previewQr: 'Voir le QR',
    downloadSheet: 'Planche à imprimer',
    downloadPng: 'Télécharger PNG',
    downloadSvg: 'Télécharger SVG',
    rotate: 'Régénérer le QR',
    rotateConfirm:
      "L'ancien QR code cessera immédiatement de fonctionner. Vous devrez le réimprimer.",
    closeSession: 'Libérer la table',
    deleteTable: 'Supprimer',
    deleteTableConfirm: (label: string) => `Supprimer la table ${label} ?`,
    deleteTableBlocked: 'Impossible de supprimer cette table : elle porte des commandes.',
    pdfUnavailable: 'Export PDF indisponible.',
    qrUnavailable: 'Le QR code est momentanément indisponible. Réessayez.',
    copyToken: 'Copier le code de la table',
    copied: 'Code copié dans le presse-papier.',
    tokenHint: 'Collez ce code dans la saisie manuelle du web-client pour tester le parcours client.',
    save: 'Enregistrer',
    cancel: 'Annuler',
    moreActions: 'Plus d\'actions',
    empty: 'Aucune table. Ajoutez la première pour commencer.',
    inactive: 'Inactive',
    free: 'Libre',
    diners: (n: number) => `${n} convive${n > 1 ? 's' : ''}`,
    pending: (n: number, total: string) =>
      `${n} commande${n > 1 ? 's' : ''} en cours · ${total}`,
  },

  report: {
    title: 'Bilan de journée',
    averagePreparation: (minutes: number) => `Préparation moyenne : ${minutes} min`,
    hourLabel: (hour: number, orders: number, revenue: string) =>
      `${hour}h : ${orders} commande${orders > 1 ? 's' : ''}, ${revenue}`,
    peak: (hour: number, orders: number) =>
      `Pic d'activité à ${hour}h, ${orders} commande${orders > 1 ? 's' : ''}.`,
    soldCount: (n: number) => `${n} vendu${n > 1 ? 's' : ''}`,
    seeAllOrders: (n: number) => `Voir les ${n} commandes`,
    downloadReceiptFor: (n: number) => `Télécharger le reçu de la commande n° ${n}`,
    revenue: "Chiffre d'affaires",
    completed: 'Terminées',
    cancelled: 'Annulées',
    averageBasket: 'Panier moyen',
    topItems: 'Plats les plus vendus',
    topItemName: 'Plat',
    topItemQuantity: 'Qté',
    topItemRevenue: 'CA',
    payments: 'Encaissements',
    paymentMethod: {
      MOBILE_MONEY: 'Mobile Money',
      COUNTER: 'Au comptoir',
      CASH: 'Espèces',
      CARD: 'Carte',
      OTHER: 'Autre',
    } satisfies Record<PaymentMethod, string>,
    completedOrders: 'Commandes terminées',
    orderNumber: 'Commande',
    orderTable: 'Table',
    orderTime: 'Heure',
    orderTotal: 'Total',
    orderPayment: 'Paiement',
    orderReceipt: 'Reçu',
    downloadReceipt: 'Télécharger',
    noPaymentMethod: 'Non renseigné',
    hourly: 'Activité par heure',
    exportPdf: 'Exporter en PDF',
    previousDay: 'Jour précédent',
    nextDay: 'Jour suivant',
    empty: 'Aucune commande terminée sur cette journée.',
    aggregatedTitle: {
      weekly: 'Bilan hebdomadaire',
      monthly: 'Bilan mensuel',
      yearly: 'Bilan annuel',
    } satisfies Record<ReportPeriod, string>,
    emptyAggregated: 'Aucune commande terminée sur cette période.',
    previousPeriod: 'Période précédente',
    nextPeriod: 'Période suivante',
    breakdown: {
      weekly: 'Détail par semaine',
      monthly: 'Détail par mois',
      yearly: 'Détail par mois',
    } satisfies Record<ReportPeriod, string>,
    periodLabel: 'Période',
    tabDaily: 'Journalier',
    tabWeekly: 'Hebdomadaire',
    tabMonthly: 'Mensuel',
    tabYearly: 'Annuel',
  },

  invoices: {
    title: 'Factures',
    number: 'Numéro',
    date: 'Date',
    total: 'Total',
    download: 'Télécharger',
    empty: 'Aucune facture.',
    pdfUnavailable: 'Le PDF n\'est pas encore disponible pour cette facture.',
  },

  history: {
    title: 'Historique des commandes',
    subtitle: 'Commandes servies, terminées et annulées, les plus récentes en premier.',
    pageOf: (page: number, total: number) => `Page ${page} sur ${total}`,
    orderColumn: 'Commande',
    tableColumn: 'Table',
    timeColumn: 'Heure',
    itemsColumn: 'Articles',
    totalColumn: 'Total',
    paymentColumn: 'Paiement',
    statusColumn: 'Statut',
    empty: 'Aucune commande terminée ce jour de service.',
    previousPage: 'Page précédente',
    nextPage: 'Page suivante',
  },

  team: {
    title: 'Équipe',
    subtitle: 'Comptes du personnel et rôles.',
    count: (n: number) => `${n} compte${n > 1 ? 's' : ''} actif${n > 1 ? 's' : ''}`,
    empty: 'Aucun compte pour le moment.',
    member: 'Membre',
    statusLabel: 'Statut',
    lastLogin: 'Dernière connexion',
    activeState: 'Actif',
    inactiveState: 'Désactivé',
    addHelp: 'Le membre se connecte avec son email et le mot de passe choisi ici.',
    deactivateHelp: "Le compte ne pourra plus se connecter. L'historique de ses actions est conservé.",
    addAccount: 'Ajouter un compte',
    editAccount: 'Modifier le compte',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'Email',
    password: 'Mot de passe',
    passwordHint: '(laisser vide pour conserver)',
    roleLabel: 'Rôle',
    active: 'Actif',
    actions: 'Actions',
    save: 'Enregistrer',
    cancel: 'Annuler',
    edit: 'Modifier',
    deactivate: 'Désactiver',
    deactivateConfirm: (name: string) => `Désactiver le compte de ${name} ?`,
    emailTaken: 'Cette adresse email est déjà utilisée.',
    yes: 'Oui',
    no: 'Non',
    roleLabels: {
      OWNER: 'Propriétaire',
      MANAGER: 'Manager',
      STAFF: 'Personnel',
      PLATFORM_ADMIN: 'Admin plateforme',
    } as Record<string, string>,
  },

  auth: {
    restoring: 'Ouverture de votre espace...',
    brandTitle: 'Votre restaurant, à portée de QR code.',
    brandText: 'Gérez vos commandes, vos menus et la présentation de votre restaurant, simplement.',
    brandPoints: [
      'Les commandes arrivent en temps réel, avec la table et les notes.',
      'Un plat épuisé disparaît du menu en un geste.',
      'Le bilan de la journée est prêt à la fermeture.',
    ],
    intro: 'Connectez-vous avec le compte remis par votre gérant.',
    backToVitrine: 'Découvrir Scan2Eat',
    title: 'Connexion',
    email: 'Email',
    password: 'Mot de passe',
    submit: 'Se connecter',
    loggingIn: 'Connexion...',
    invalidCredentials: 'Email ou mot de passe incorrect.',
    accountDisabled: 'Ce compte est désactivé. Contactez le gérant.',
    rateLimited: 'Trop de tentatives. Réessayez dans une minute.',
  },

  profile: {
    subtitle: 'Vos informations personnelles et votre mot de passe.',
    identityHelp: 'Votre nom apparaît dans l\'historique des commandes.',
    changePassword: 'Changer le mot de passe',
    noRestaurant: 'Aucun — administrateur de plateforme',
    title: 'Mon profil',
    photoAlt: 'Photo de profil',
    initialsPlaceholder: 'Photo non renseignée',
    sectionIdentity: 'Informations',
    sectionPassword: 'Changer le mot de passe',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'Email',
    role: 'Rôle',
    restaurant: 'Restaurant',
    lastLogin: 'Dernière connexion',
    neverConnected: 'Jamais',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le nouveau mot de passe',
    passwordHint: '10 caractères minimum, avec minuscules, majuscules et chiffres.',
    mismatch: 'La confirmation ne correspond pas au nouveau mot de passe.',
    invalidCurrentPassword: 'Le mot de passe actuel est incorrect.',
    save: 'Enregistrer',
    cancel: 'Annuler',
    saved: 'Profil mis à jour.',
    passwordChanged: 'Mot de passe modifié.',
  },

  /**
   * Palette de marque du menu client, proposée par une IA. À ne pas
   * confondre avec `theme` (clair/sombre, propre à web-admin). Voir
   * docs/adr/0025.
   */

  errors: {
    forbiddenTitle: 'Accès réservé',
    notFound: "Cette page n'existe pas ou a été déplacée.",
    backHome: 'Retour aux commandes',
    forbidden: "Vous n'avez pas les droits nécessaires pour cette action.",
    staleTransition: 'Cette commande a déjà changé de statut. La vue a été actualisée.',
    network: 'Connexion perdue. Vérifiez votre réseau, puis réessayez.',
    connectionLost: 'Connexion perdue, reconnexion en cours',
    generic: 'Une erreur est survenue. Réessayez dans un instant.',
    retry: 'Réessayer',
  },
} as const;
